import React, { useState, useRef, useEffect } from "react";
import SimpleReactValidator from 'simple-react-validator';
import { Input, LabelText, SubmitButton, FlexContainer, H1Text, Text, Img, Spinner } from "../../design";
import TransactionModal from "../../components/modal/transaction";
import SuccessModal from "../../components/modal/success";
import BulletIcon from "../../assets/imgs/bullet.svg";
import { addOrumVerification, getOrumVerificationStatus } from "../../api";
import { SWIREPAY_URL, MESSAGES } from "../../appconstants";
import BaseSplitPage from "../../components/splitpage";
import Header from "./smallscreen/header";
import Support from "../../components/support";
import PaymentError from "../../components/paymenterror";
import ErrorMessageModal from "../../components/modal/errorMessage";
import SuccessMessageModal from "../../components/modal/successMessage";


type Props = {
    history: {
        push: Function,
    },
}

const AccountVerification = (props: Props) => {
    const [, forceUpdate] = useState();
    const [isTransactionModalOpen, setTransactionModalOpen] = useState(false);
    const [isTransactionSuccessModalOpen, setTransactionSuccessModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showerrorMessage, setShowErrorMessage] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [isShowSpinner, setShowSpinner] = useState(false);
    const [orumVerificationLinkStatus, setOrumVerificationLinkStatus] = useState('');

    const { match: { params: { id } }, location: { pathname } } = props;

    const simpleValidator = useRef(new SimpleReactValidator({
        validators: {
            validUrl: {
                message: 'Please enter a valid Business Website',
                rule: val => (new RegExp('^(https?://)?(www\\.)?([-a-z0-9]{1,63}\\.)*?[a-z0-9][-a-z0-9]{0,61}[a-z0-9]\\.[a-z]{2,6}(/[-\\w@\\+\\.~#\\?&/=%]*)?$').exec(val) !== null),
            },
            confirmAccount: {
                message: 'Must match account number typed above',
                rule: (val, params) => val === params[0],
            },
    }}));

    simpleValidator.current.purgeFields();

    const confirm = async () => {
            if (orumVerificationLinkStatus !== 'pending') return;
            const formValid = simpleValidator.current.allValid();
            if (!formValid) {
                simpleValidator.current.showMessages();
                forceUpdate(1);
                return;
            }
            simpleValidator.current.hideMessages();
            setShowErrorMessage(false);
            forceUpdate();
        
            let params = {
                code: verificationCode
            };
            setShowSpinner(true);
            try {
                const entity = await addOrumVerification(params, id);
                if (entity && entity.status === 'VALID') {
                    setTransactionModalOpen(true);
                };
                const { redirectUri } = entity;
                if (redirectUri) {
                    window.location = redirectUri;
                    return;
                }
                setTransactionModalOpen(false);
                setTransactionSuccessModalOpen(true);
                setTimeout(function () {
                    if (entity?.redirectUri) {
                        window.location = `${entity?.redirectUri}`;
                    } else {
                        window.location = `${SWIREPAY_URL}`;
                    }
                }, 5000);
                localStorage.removeItem('publicKey');
            } catch (reason) {
                setShowErrorMessage(true);
                setErrorMessage(reason && reason.data ? reason.data.message : 'Unable to process request. Please try again later');
            } finally {
                setShowSpinner(false);
            }
    };

    const containerStyle = {
        border: '1px solid #ddd',
        padding: '15px',
        borderRadius: '4px',
        backgroundColor: '#F2F2F9',
        margin: '20px 0',
      };
    
      const titleStyle = {
        fontWeight: 'bold',
        marginBottom: '10px',
      };
    
      const transactionStyle = {
        border: '1px solid #ddd',
        padding: '8px',
        borderRadius: '4px',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginBottom: '25px',
        width: '19rem'
      };
    
      const textStyle = {
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
      };
    
      const highlightStyle = {
        fontSize: '20px',
        fontWeight: 'bold',
        color: 'rgb(64 169 255)',
        borderRadius: '4px',
        border: '2px solid red',
        padding: '2px 6px',
        position: 'relative',
      };
    
      const arrowStyle = {
        position: 'absolute',
        bottom: '-20px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '2px',
        height: '20px',
        backgroundColor: 'black',
      };
    
      const arrowHeadStyle = {
        position: 'absolute',
        bottom: '-24px',
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '8px solid black',
      };
    
      const labelStyle = {
        position: 'absolute',
        bottom: '-40px',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '12px',
        color: '#1A2028',
      };

    // to check if the verification link is still valid and active
    useEffect(() => {
        getOrumVerificationStatus(id)
        .then((entity) => {
            switch (entity.status) {
                case 'CANCELED':
                    setOrumVerificationLinkStatus('cancelled');
                    break;
                case 'VALID':
                    setOrumVerificationLinkStatus('valid');
                    break;
                case 'PENDING':
                    setOrumVerificationLinkStatus('pending');
                    break;
                default:
                    setOrumVerificationLinkStatus('');
            }
        });
    }, [id]);
    
  
    return (
        <>
            <ErrorMessageModal
                isOpen={orumVerificationLinkStatus === 'cancelled'}
                // isOpen={orumVerificationLinkStatus === 'pending'} // for testing
                errorMsg={MESSAGES.ORUM_VERIFICATION_LINK_STATUS.ERROR}
            />
            <SuccessMessageModal
                isOpen={orumVerificationLinkStatus === 'valid'}
                // isOpen={orumVerificationLinkStatus === 'pending'} // for testing
                successMsg={MESSAGES.ORUM_VERIFICATION_LINK_STATUS.SUCCESS}
            />
            <BaseSplitPage left={
                <>
                    <FlexContainer
                        flexDirection="column"
                        position="relative"
                        display="block"
                        height="100%"
                    >
                        <div>
                            <H1Text>
                                ACCOUNT VERIFICATION
                            </H1Text>
                        </div>
                        <div style={{ position: "sticky", top: "85%" }}>
                            <Support email={null}
                                phone={null} />
                        </div>
                    </FlexContainer>
                </>
            } right={
                <>
                    <Header payment={null} />
                    <FlexContainer
                        className="pt-3 pt-lg-5"
                        box-shadow="0 0 15px 0 rgba(0, 0, 0, 0.1)"
                        display="flow-root"
                    >

                        <H1Text
                            className="px-1 px-md-4 mt-4"
                        >
                            VERIFY YOUR BANK ACCOUNT INSTANTLY
                        </H1Text>
                        <div className="px-1 px-md-4 mt-2">
                            <FlexContainer
                                flexDirection="column"
                            >
                                <Text fontSize="15px" marginTop="4px" marginBottom="2px">
                                Find the $0.01 deposit in your bank account. The 4-digit code you need is the last four characters after the dash in the transaction description.
                                </Text>
                                <div style={containerStyle}>
                                    <div style={titleStyle}>Recent transactions</div>
                                    <div style={transactionStyle}>
                                        <span style={textStyle}><span style={highlightStyle}>a9d3
                                        <div style={arrowStyle}/>
                                        <div style={arrowHeadStyle}></div>
                                        <div style={labelStyle}>YOUR CODE</div>
                                        </span>-swirepay -ACCTVERIFY
                                        </span>
                                        <span>$0.01</span>
                                    </div>
                                </div>
                                <Text fontSize="15px" marginTop="2px" marginBottom="4px">
                                <Img verticalAlign='middle' src={BulletIcon} /> Enter the 4-digit code below to complete verification.
                                </Text>
                                <div>
                                    <LabelText className="mb-1">
                                        Verification Code
                                    </LabelText>
                                    <Input
                                        style={{
                                            //width: '208px',
                                            height: '36px',
                                            border: '1px solid #C4C4C4',
                                            borderRadius: '6px',
                                            width: '21rem'
                                        }}
                                        value={verificationCode}
                                        onChange={(e) => {
                                            setVerificationCode(e.target.value);
                                        }}
                                    />
                                    {
                                    <Text fontSize="12px" marginTop="4px" marginBottom="0px" color="#ff1e1e">
                                        {simpleValidator.current.message('verification code', verificationCode, `required`)}
                                    </Text>
                                    }
                                </div>
                                <div style={{ paddingLeft: '0px', paddingTop: '8px' }}>
                                    <SubmitButton
                                        type="submit"
                                        marginTop="0px !importnat"
                                        // style={{ width: '142px' }}
                                        style={{ width: '21rem' }}
                                        onClick={() => confirm()}
                                    >CONFIRM</SubmitButton>
                                </div>
                            </FlexContainer>
                        </div>
                    </FlexContainer>
                    <TransactionModal body={null} header={null} isOpen={isTransactionModalOpen} />
                    <SuccessModal successMsg="Verification Completed" isOpen={isTransactionSuccessModalOpen} partnerConnection={true} />
                    <Spinner isOpen={isShowSpinner} />
                    {showerrorMessage && <PaymentError header={"Error"} message={errorMessage} />}
                </>
            } />
        </>
    )
};

export default AccountVerification;
